import {
  File,
  StoragePathType,
  useGenerateUploadUrlMutation,
  useGetAllowedUploadSpecificationsQuery,
  useMakeFilePublicMutation,
} from "../graphql/generated/graphql";
import * as DocumentPicker from "expo-document-picker";
import axios from "axios";
import { useAppConfig } from "../AppConfigProvider";
import { useEffect } from "react";

export const generatePublicUrl = (currentDomain: string, filePath: string) => {
  let publicUrl = "";
  if (currentDomain === "axiodev.xyz") {
    publicUrl = `http://localhost:4443/storage/v1/b/primerai-dev-storage/o/${filePath}?alt=media`;
  } else {
    publicUrl = `https://${currentDomain}/${filePath}`;
  }

  return publicUrl;
};

export interface ProcessedFile {
  file: File;
  signedUrl: string;
  mimeType: string;
  size: number;
  localUploadedFile: DocumentPicker.DocumentPickerAsset;
}

export type UseFileUploadHookProps = {
  storagePath: StoragePathType;
  beforeUpload?: () => void;
  afterUpload?: (processedFilesArray: ProcessedFile[]) => void;
  onError?: (message?: string) => void;
  isPublic?: boolean;
  multiple?: boolean;
};

const useFileUpload = ({
  storagePath,
  beforeUpload = () => {},
  afterUpload = (processedFilesArray: ProcessedFile[]) => {},
  onError = (message?: string) => {},
  isPublic = false,
  multiple = false,
}: UseFileUploadHookProps) => {
  const { currentDomain } = useAppConfig();
  const [generateUploadUrl] = useGenerateUploadUrlMutation();
  const [makeFilePublic] = useMakeFilePublicMutation();

  const { data: uploadSpecificationsData, refetch: uploadSpecificationsRefetch } =
    useGetAllowedUploadSpecificationsQuery({
      variables: {
        storagePath,
      },
      skip: !storagePath,
    });

  useEffect(() => {
    if (storagePath) {
      uploadSpecificationsRefetch();
    }
  }, [storagePath, uploadSpecificationsRefetch]);

  const allowedMimeTypes = uploadSpecificationsData?.getAllowedUploadSpecifications?.data?.allowedMimeTypes ?? [];
  const maxFileSize = uploadSpecificationsData?.getAllowedUploadSpecifications?.data?.maxFileSize ?? 0;

  const validateFile = (mimeType: string, size: number) => {
    // Only enforce size constraint when backend provided a positive limit
    const isSizeConstraintEnabled = !!maxFileSize && maxFileSize > 0;
    if (isSizeConstraintEnabled && size > maxFileSize) {
      onError?.(
        `File size must be under ${maxFileSize / 1024 / 1024}MB. Yours is ${(size / 1024 / 1024).toFixed(2)}MB.`
      );
      return false;
    }
    // Only enforce type constraint when backend provided accepted MIME types
    const isTypeConstraintEnabled = Array.isArray(allowedMimeTypes) && allowedMimeTypes.length > 0;
    if (isTypeConstraintEnabled && !allowedMimeTypes.includes(mimeType)) {
      onError?.(`Invalid file type. Accepted file types: ${allowedMimeTypes.join(", ")}.`);
      return false;
    }
    return true;
  };

  const handleServerUpload = async ({
    uri,
    mimeType,
    size,
    name,
  }: {
    uri: string;
    mimeType: string;
    size: number;
    name: string;
  }) => {
    try {
      if (!validateFile(mimeType, size)) {
        return null;
      }

      const data = await generateUploadUrl({
        variables: {
          fileName: name,
          storagePath: storagePath,
        },
      });

      const uploadData = data?.data?.generateUploadUrl?.data[0];
      const { file, signedUrl, uploadedFileMimeType } = uploadData || {};

      if (!signedUrl) throw new Error("Failed to get signed URL");

      // Upload file to GCP using signed URL
      const fileData = await fetch(uri);
      const blob = await fileData.blob();

      await axios.put(signedUrl, blob, {
        headers: {
          "Content-Type": uploadedFileMimeType || mimeType,
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        },
      });

      if (isPublic && currentDomain !== "axiodev.xyz") {
        const result = await makeFilePublic({
          variables: {
            fileId: file.id,
          },
        });

        if (!result.data?.makeFilePublic?.success) {
          throw new Error("Failed to make file public");
        }
      }

      return { file: file as File, signedUrl };
    } catch (error) {
      onError?.(error?.message || "An error occurred during file upload");
      return null;
    }
  };

  const handleFileUpload = async () => {
    await beforeUpload();
    try {
      const result = await DocumentPicker.getDocumentAsync({
        // When constraints haven't loaded yet, allow all types in the picker
        type: allowedMimeTypes && allowedMimeTypes.length > 0 ? allowedMimeTypes.join(",") : "*/*",
        copyToCacheDirectory: true,
        multiple,
      });

      if (result.canceled || !result.assets) {
        return;
      }

      const processedFilesArray: {
        file: File;
        signedUrl: string;
        mimeType: string;
        size: number;
        localUploadedFile: DocumentPicker.DocumentPickerAsset;
      }[] = [];

      for (const asset of result.assets) {
        const { uri, mimeType, size, name } = asset;

        if (!validateFile(mimeType, size)) {
          return;
        }

        const { file, signedUrl } = await handleServerUpload({ uri, mimeType, size, name });

        processedFilesArray.push({
          file: file,
          signedUrl,
          size,
          mimeType,
          localUploadedFile: asset,
        });
      }

      await afterUpload(processedFilesArray);

      return processedFilesArray;
    } catch (error) {
      onError?.(error?.message || "An error occurred during file upload");
    }
  };

  return { handleFileUpload, handleServerUpload, allowedMimeTypes, maxFileSize };
};

export default useFileUpload;
